import { get } from 'lodash';
import { trackConfig, type IFramework } from '@/framework';
import { checkIsHorizontalMultiLine, TextMeasurer } from '@/utils/text-kit';
import { LayerType, TextWritingType, type EditingRange, type ILayerProps, type ITextLayerProps } from '@/version-types';
import { pushHistory, RichTextProcessor, updateGroupLayer, updateLayer, validateInputLayer } from '../utils';
import { type IShapeText } from './shape-text';

export interface ITextDecoration {
    /**
     * 设置下划线
     * @param layerID - 图层 id
     * @param flag - 是否设置下划线
     * @param options - 配置项
     * @returns
     */
    __setUnderline(
        layerID: string,
        flag: boolean,
        options?: { range?: EditingRange | null; writeHistory?: boolean },
    ): Promise<{ layer: ILayerProps; range: EditingRange | null; writeHistory?: boolean } | void>;

    /**
     * 设置删除线
     * @param layerID - 图层 id
     * @param flag - 是否设置删除线
     * @param options - 配置项
     * @returns
     */
    __setLineThrough(
        layerID: string,
        flag: boolean,
        options?: { range?: EditingRange | null; writeHistory?: boolean },
    ): Promise<{ layer: ILayerProps; range: EditingRange | null; writeHistory?: boolean } | void>;
}

export class TextDecoration implements ITextDecoration {
    constructor(
        private framework: IFramework,
        private shapeText: IShapeText,
    ) {}

    private resolveConflict(
        layerData: ITextLayerProps,
        kind: 'underline' | 'lineThrough',
        flag: boolean,
        range: EditingRange | null,
    ) {
        if (!flag) return layerData;

        const conflictType = kind === 'underline' ? 'lineThrough' : 'underline';
        const nextLayerData = { ...layerData };

        nextLayerData[conflictType] = false;

        const { textContents = [] } = layerData;
        if (!range && textContents.length > 0) {
            nextLayerData.textContents = textContents.map((item) => ({
                ...item,
                [conflictType]: false,
            }));
        }

        if (!range || textContents.length === 0) return nextLayerData;

        const { startIndex, endIndex } = range;
        nextLayerData.textContents = textContents.map((item, idx) => {
            if (idx < startIndex || idx > endIndex) return item;
            return {
                ...item,
                [conflictType]: false,
            };
        });

        return nextLayerData;
    }

    private calcLayout(layer: ILayerProps) {
        const { width: layerWidth, height: layerHeight } = layer;
        const layerData = layer.data as ITextLayerProps;
        const { writingMode, fontSize, lineSpacing } = layerData;

        const measureOptions = {
            keepConstantWidth: false,
            keepConstantHeight: false,
        };

        if (
            writingMode === TextWritingType.HORIZONTAL_TB &&
            (get(layer, '__fixedDirectionSize', false) ||
                checkIsHorizontalMultiLine(layerHeight, fontSize, lineSpacing))
        ) {
            measureOptions.keepConstantWidth = true;
        }

        const textSize = TextMeasurer.measure(layer, measureOptions);
        const nextLayerWidth = Math.max(layerWidth, textSize.width);
        const nextLayerHeight = Math.max(layerHeight, textSize.height);

        return {
            width: nextLayerWidth,
            height: nextLayerHeight,
        };
    }

    private async set(
        prevLayer: ILayerProps,
        kind: 'underline' | 'lineThrough',
        flag: boolean,
        options: { range?: EditingRange | null; activate?: boolean; writeHistory?: boolean } = {},
    ) {
        const { writeHistory = false, activate = true } = options;
        const range = options.range || null;

        const prevLayerData = prevLayer.data as ITextLayerProps;

        const { texture, textGradient } = prevLayerData;

        // 使用纹理时不允许设置下划线和删除线
        if (texture && texture.url) return;

        // 使用渐变时不允许设置下划线和删除线
        if (textGradient && textGradient.colors.length > 1) return;

        let nextLayerData: ITextLayerProps = { ...prevLayerData };
        let nextRange: EditingRange | null = null;

        if (range && !range.isCollapsed) {
            const result = RichTextProcessor.process(nextLayerData, range, {
                key: kind,
                value: flag,
            });
            nextLayerData = { ...nextLayerData, textContents: result.textContents };
            nextRange = result.range;
        } else {
            const prevValue = prevLayerData[kind];

            const { textContents: prevTextContents = [] } = prevLayerData;
            if (prevTextContents.length > 0) {
                const values = prevTextContents.map((item) => item[kind]);
                const set = new Set(values);

                if (set.size === 1 && values[0] === flag) return;

                const nextTextContents = prevTextContents.map((item) => ({
                    ...item,
                    [kind]: flag,
                }));

                nextLayerData = { ...nextLayerData, textContents: nextTextContents };

                // 有 textContents 时，不设置全局属性的 lineThrough、underline
                // 否则渲染层上会出现重复的下划线或删除线
                if (flag) nextLayerData = { ...nextLayerData, [kind]: false };
            } else {
                if (prevValue === flag) return;
                nextLayerData = { ...nextLayerData, [kind]: flag };
            }
        }

        // resolve conflict
        nextLayerData = this.resolveConflict(nextLayerData, kind, flag, nextRange);
        let nextLayer: ILayerProps = { ...prevLayer, data: nextLayerData };

        const { shapeExtension } = prevLayerData;
        if (!shapeExtension) {
            const { width, height } = this.calcLayout(nextLayer);
            nextLayer.width = width;
            nextLayer.height = height;
        } else {
            const layer = await this.shapeText.refreshLayerScreenshotURL(nextLayer);
            if (layer) nextLayer = layer;
        }

        updateLayer(this.framework, nextLayer, { activate });
        if (writeHistory) pushHistory(this.framework, prevLayer, nextLayer, trackConfig[LayerType.TEXT_LAYER][kind]);

        return { layer: nextLayer, range: nextRange };
    }

    private async setDecoration(
        layerID: string,
        kind: 'underline' | 'lineThrough',
        flag: boolean,
        options: { range?: EditingRange | null; update?: boolean; writeHistory?: boolean },
    ) {
        const prevLayer = this.framework.getLayer(layerID, true);
        if (!validateInputLayer(prevLayer)) return;

        if (prevLayer.layerType === LayerType.GROUP_LAYER) {
            const children = prevLayer.children;
            if (children.length === 0) return;

            const results = await Promise.all(
                children.map((child) => this.set(child, kind, flag, { activate: false })),
            );
            const layers = results.map((result) => (result ? result.layer : null)).filter(Boolean) as ILayerProps[];

            const nextLayer = updateGroupLayer(this.framework, prevLayer, layers, {
                funcID: trackConfig[LayerType.TEXT_LAYER][kind],
            });

            return { layer: nextLayer, range: null };
        }

        const { range, writeHistory } = options;
        return this.set(prevLayer, kind, flag, { range, activate: true, writeHistory });
    }

    async __setUnderline(layerID: string, flag: boolean, options = { range: null, writeHistory: true }) {
        return this.setDecoration(layerID, 'underline', flag, options);
    }

    async __setLineThrough(layerID: string, flag: boolean, options = { range: null, writeHistory: true }) {
        return this.setDecoration(layerID, 'lineThrough', flag, options);
    }
}
